import type { NextPage } from 'next';
import styles from '../../styles/form/textInput.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, library } from '@fortawesome/fontawesome-svg-core';
import { joinClassnames } from 'helpers/classNames';
import { faLock, faEnvelope, faUser, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { Size } from 'types/size';
import TextareaAutosize from 'react-textarea-autosize';
import { Type } from 'types/type';

library.add(faLock, faEnvelope, faUser, faPlus);

interface Props {
  text: string;
  placeholder?: string;
  onChange: (value: string) => void; // Specific type for onChange
  icon?: IconName;
  type?: string;
  color?: string;
  label?: string;
  smallPadding?: boolean;
  required?: boolean;
  disabled?: boolean;
  size?: Size;
  maxLength?: number;
  colorType?: Type;
  border?: boolean;
  shadow?: boolean;
}

const TextAreaComponent: NextPage<Props> = ({
  text,
  placeholder,
  onChange,
  icon,
  color,
  label,
  smallPadding,
  required = false,
  disabled = false,
  maxLength = 2000,
  colorType,
  border = false,
  shadow = false,
}) => {
  const [value, setValue] = useState(text);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const textInputClassName = joinClassnames([
    styles.text_input,
    icon ? styles.text_input_with_icon : null,
    smallPadding ? styles.small_padding : styles.input_padding,
    disabled && styles.disabled,
    colorType === Type.t1 && styles.text_input_type1,
    colorType === Type.t2 && styles.text_input_type2,
    colorType === Type.t3 && styles.text_input_type3,
    border && styles.shadow_border,
    shadow && styles.shadow,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {required && '* '}
          {label}
        </label>
      )}
      <div>
        {icon && (
          <div className={styles.icon_wrapper}>
            <FontAwesomeIcon className={styles.icon} icon={['fas', icon]} />
          </div>
        )}
        <TextareaAutosize
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          className={textInputClassName}
          style={{ backgroundColor: color, fontSize: '0.9rem' }}
          onChange={handleChange}
          maxLength={maxLength} // Use maxLength on the TextareaAutosize for better UX
        />
      </div>
    </div>
  );
};

export default TextAreaComponent;
